import React, { useEffect, useState } from "react";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Trash2, RotateCcw, History } from "lucide-react";
import {
  listDeletedHoles,
  removeDeletedHole,
  stripBuiltins,
} from "@/lib/deletedBackup";

export default function RecentlyDeletedHoles({ onChanged }) {
  const [items, setItems] = useState([]);
  const [restoring, setRestoring] = useState(null);

  const reload = () => setItems(listDeletedHoles());
  useEffect(() => {
    reload();
  }, []);

  const handleRestore = async (entry) => {
    setRestoring(entry.id);
    try {
      const newSheet = await base44.entities.Sheet.create(stripBuiltins(entry.sheet));
      const newRuns = [];
      for (const run of entry.runs || []) {
        const payload = { ...stripBuiltins(run), sheet_id: newSheet.id };
        newRuns.push(payload);
      }
      if (newRuns.length) {
        await base44.entities.DrillRun.bulkCreate(newRuns);
      }
      removeDeletedHole(entry.id);
      reload();
      onChanged?.();
    } catch (e) {
      console.error(e);
    } finally {
      setRestoring(null);
    }
  };

  const handleDiscard = (entry) => {
    removeDeletedHole(entry.id);
    reload();
  };

  if (items.length === 0) return null;

  return (
    <div className="mt-6 space-y-3">
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2 dark:text-slate-400">
        <History className="w-4 h-4" /> Recently deleted
      </h3>
      <div className="space-y-2">
        {items.map((entry) => (
          <div
            key={entry.id}
            className="bg-white rounded-2xl border border-slate-200 p-3 flex items-center justify-between gap-3 dark:bg-slate-900 dark:border-slate-800"
          >
            <div className="min-w-0">
              <h4 className="font-bold text-slate-900 truncate dark:text-slate-100">
                {entry.sheet?.name || "Untitled hole"}
              </h4>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {(entry.runs?.length || 0)} runs · deleted{" "}
                {new Date(entry.deletedAt).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleRestore(entry)}
                disabled={restoring === entry.id}
                className="h-11 w-11 md:h-9 md:w-9"
                title="Restore"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDiscard(entry)}
                className="h-11 w-11 md:h-9 md:w-9 text-red-500 hover:text-red-600"
                title="Delete permanently"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}